import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import axios from 'axios';
import { useAuth } from '../context/AuthContext';
import VideoCard from '../components/VideoCard';
import { Button } from '../components/ui/button';
import { 
  User, BadgeCheck, MessageCircle, Heart, 
  Eye, Video, Loader2, Twitter, Instagram, ExternalLink
} from 'lucide-react';
import { toast } from 'sonner';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const CreatorProfile = () => {
  const { userId } = useParams();
  const navigate = useNavigate();
  const { user, isAuthenticated } = useAuth();
  const [profile, setProfile] = useState(null);
  const [videos, setVideos] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fetchProfile();
  }, [userId]);

  const fetchProfile = async () => {
    try {
      const response = await axios.get(`${API}/creator/profile/${userId}`);
      setProfile(response.data.profile);
      setVideos(response.data.videos || []);
    } catch (error) {
      toast.error('Creator not found');
      navigate('/creators');
    } finally {
      setLoading(false);
    }
  };

  const handleMessage = () => {
    if (!isAuthenticated) {
      toast.error('Please sign in to send messages');
      navigate('/login');
      return;
    }
    navigate(`/messages/${userId}`);
  };

  if (loading) {
    return (
      <div className="min-h-screen pt-20 flex items-center justify-center">
        <Loader2 className="w-8 h-8 animate-spin text-gray-400" />
      </div>
    );
  }

  if (!profile) {
    return null;
  }

  return (
    <div className="min-h-screen pt-20" data-testid="creator-profile">
      {/* Cover Image */}
      <div 
        className="h-48 md:h-64 bg-gradient-to-br from-[#1A1A1A] to-[#0A0A0A] relative"
        style={profile.cover_image ? { backgroundImage: `url(${profile.cover_image})`, backgroundSize: 'cover', backgroundPosition: 'center' } : {}}
      >
        <div className="absolute inset-0 bg-gradient-to-t from-[#050505] to-transparent" />
      </div>

      {/* Profile Header */}
      <div className="px-4 md:px-8 lg:px-16 -mt-16 relative z-10">
        <div className="max-w-6xl mx-auto">
          <div className="flex flex-col md:flex-row gap-6 items-start">
            {/* Avatar */}
            <div className="w-32 h-32 rounded-full border-4 border-[#050505] bg-gradient-to-br from-gray-600 to-gray-800 flex items-center justify-center overflow-hidden">
              {profile.avatar ? (
                <img src={profile.avatar} alt={profile.display_name} className="w-full h-full object-cover" />
              ) : (
                <User className="w-12 h-12 text-white" />
              )}
            </div>

            {/* Info */}
            <div className="flex-1">
              <div className="flex items-center gap-3 mb-2">
                <h1 
                  className="text-3xl md:text-4xl font-normal tracking-wide uppercase text-white"
                  style={{ fontFamily: 'Bebas Neue, sans-serif' }}
                >
                  {profile.display_name}
                </h1>
                {profile.verified && (
                  <BadgeCheck className="w-6 h-6 text-amber-500" />
                )}
              </div>

              {profile.bio && (
                <p className="text-gray-400 mb-4 max-w-2xl">{profile.bio}</p>
              )}

              {/* Stats */}
              <div className="flex gap-6 mb-4">
                <div className="text-center">
                  <p className="text-xl font-bold text-white">{videos.length}</p>
                  <p className="text-gray-500 text-sm">Videos</p>
                </div>
                <div className="text-center">
                  <p className="text-xl font-bold text-white">{profile.follower_count || 0}</p>
                  <p className="text-gray-500 text-sm">Followers</p>
                </div>
                <div className="text-center">
                  <p className="text-xl font-bold text-white">{profile.total_views || 0}</p>
                  <p className="text-gray-500 text-sm">Views</p>
                </div>
              </div>

              {/* Social Links */}
              {profile.social_links && Object.keys(profile.social_links).length > 0 && (
                <div className="flex gap-3 mb-4">
                  {profile.social_links.twitter && (
                    <a href={profile.social_links.twitter} target="_blank" rel="noopener noreferrer" className="p-2 bg-white/10 rounded-full hover:bg-white/20 transition-colors">
                      <Twitter className="w-4 h-4 text-white" />
                    </a>
                  )}
                  {profile.social_links.instagram && (
                    <a href={profile.social_links.instagram} target="_blank" rel="noopener noreferrer" className="p-2 bg-white/10 rounded-full hover:bg-white/20 transition-colors">
                      <Instagram className="w-4 h-4 text-white" />
                    </a>
                  )}
                  {profile.social_links.onlyfans && (
                    <a href={profile.social_links.onlyfans} target="_blank" rel="noopener noreferrer" className="p-2 bg-white/10 rounded-full hover:bg-white/20 transition-colors">
                      <ExternalLink className="w-4 h-4 text-white" />
                    </a>
                  )}
                </div>
              )}

              {/* Actions */}
              {user?.id !== userId && (
                <div className="flex gap-3">
                  <Button 
                    onClick={handleMessage}
                    className="bg-white text-black hover:bg-gray-200"
                    data-testid="message-creator-btn"
                  >
                    <MessageCircle className="w-4 h-4 mr-2" />
                    Message
                  </Button>
                </div>
              )}
            </div>
          </div>
        </div>
      </div>

      {/* Videos */}
      <div className="px-4 md:px-8 lg:px-16 py-12">
        <div className="max-w-6xl mx-auto">
          <h2 
            className="text-2xl font-normal tracking-wide uppercase mb-6"
            style={{ fontFamily: 'Bebas Neue, sans-serif' }}
          >
            Videos
          </h2>

          {videos.length === 0 ? (
            <div className="text-center py-12">
              <Video className="w-16 h-16 text-gray-600 mx-auto mb-4" />
              <p className="text-gray-400">No videos yet</p>
            </div>
          ) : (
            <div className="video-grid">
              {videos.map(video => (
                <VideoCard 
                  key={video.id} 
                  video={{
                    ...video,
                    views: video.views?.toString() || '0'
                  }} 
                />
              ))}
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default CreatorProfile;
